import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { CheckCircle2, XCircle, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Alpaca broker connection card.
 *
 * The recommended real-broker path — one paste of API key + secret and Aanar pulls
 * real balances, positions, and can route real orders. No local gateway. No tunnel.
 * Encrypted at rest server-side; the secret never leaves the browser after save.
 */
export default function AlpacaCard() {
  const [status, setStatus] = useState({ connected: false, env: null, api_key_masked: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [env, setEnv] = useState("paper");

  const load = () =>
    api.get("/broker/alpaca/settings")
      .then((r) => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (apiKey.trim().length < 10 || apiSecret.trim().length < 10) {
      toast.error("Key and Secret both need to be pasted."); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/broker/alpaca/settings", {
        api_key: apiKey.trim(), api_secret: apiSecret.trim(), env,
      });
      toast.success(`Connected to Alpaca ${data.env} · account ${data.account_number}`);
      setApiKey(""); setApiSecret(""); setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alpaca rejected those credentials.");
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Alpaca? Aanar will fall back to simulator numbers.")) return;
    try {
      await api.delete("/broker/alpaca/settings");
      toast.success("Alpaca disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  return (
    <Card className="p-6 border-border rounded-xl" data-testid="alpaca-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFD400] text-black flex items-center justify-center font-display font-bold">
            α
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-semibold">Alpaca</div>
              {status.connected && (
                <Badge variant="secondary" data-testid="alpaca-connected-badge" className="rounded-full">
                  <CheckCircle2 className="w-3 h-3 mr-1 text-[hsl(var(--gain))]" />
                  Connected · {status.env}
                </Badge>
              )}
              <Badge className="bg-[hsl(var(--gain))]/15 text-[hsl(var(--gain))] border-[hsl(var(--gain))]/30" variant="outline">
                Recommended · Real broker
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Paste an API Key + Secret to hook up your real Alpaca paper (or live) account.
              No gateway. No tunnel. Real balances, real positions, real fills.
            </p>
            {status.connected && (
              <div className="mt-3 text-xs text-muted-foreground font-mono-tab" data-testid="alpaca-masked-key">
                Key: {status.api_key_masked || "—"} · updated {status.updated_at ? new Date(status.updated_at).toLocaleString() : "—"}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          {status.connected ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)} className="rounded-full" data-testid="alpaca-reconfigure">
                Reconfigure
              </Button>
              <Button variant="ghost" size="sm" onClick={disconnect} className="rounded-full text-[hsl(var(--loss))]" data-testid="alpaca-disconnect">
                Disconnect
              </Button>
            </>
          ) : !loading && (
            <Button size="sm" onClick={() => setShowForm(true)} className="rounded-full" data-testid="alpaca-open-form">
              <ShieldCheck className="w-4 h-4 mr-1" /> Connect Alpaca
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="mt-5 pt-5 border-t border-border space-y-4" data-testid="alpaca-form">
          <div className="text-xs bg-secondary/40 border border-border rounded-md p-3 space-y-1">
            <div className="text-foreground font-medium">Get your Alpaca API keys (free, 2 minutes):</div>
            <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground">
              <li>Sign in at <a className="underline text-[hsl(var(--ai))]" href="https://app.alpaca.markets/login" target="_blank" rel="noreferrer">app.alpaca.markets <ExternalLink className="w-2.5 h-2.5 inline" /></a> (free — email only for paper).</li>
              <li>Top-right corner, switch to <b>Paper Trading</b>.</li>
              <li>Left sidebar → <b>Overview</b> → scroll to <b>API Keys</b> panel.</li>
              <li>Click <b>Generate New Key</b>. Copy the <b>Key ID</b> and <b>Secret</b> immediately (Secret shown only once).</li>
              <li>Paste both here ↓</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1">Environment</Label>
              <div className="flex gap-2">
                {["paper", "live"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setEnv(v)}
                    data-testid={`alpaca-env-${v}`}
                    className={`text-xs uppercase tracking-wider rounded-full px-3 py-1.5 border ${env === v
                      ? "bg-[hsl(var(--ai))]/15 border-[hsl(var(--ai))] text-[hsl(var(--ai))]"
                      : "border-border text-muted-foreground"}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground mb-1">API Key ID</Label>
            <Input
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="PK..."
              className="font-mono-tab text-xs"
              data-testid="alpaca-key-input"
              autoComplete="off"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1">API Secret</Label>
            <Input
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              placeholder="Long secret — shown only once when you generated it"
              className="font-mono-tab text-xs"
              data-testid="alpaca-secret-input"
              autoComplete="off"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Never sent to the browser after save. Stored encrypted server-side.
            </div>
          </div>

          {env === "live" && (
            <div className="text-xs text-[hsl(var(--loss))] bg-[hsl(var(--loss))]/10 border border-[hsl(var(--loss))]/30 rounded p-2 flex gap-2">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                You're saving a <b>LIVE</b> Alpaca key — real money will be at risk on any order routed here.
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="alpaca-save">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save & test connection
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
